import { useCallback, useMemo } from 'react';

import { ErrorTracking } from '@common/errorTracking';
import { useAbTestStore } from '../state/abTestStore';
import {
  fetchRegisterConversion,
  fetchRegisterImpression,
} from '../nextUtils/abTestUtils';
import { RemoteData } from '../types/remoteData';
import { remoteDataSelect } from '../nextUtils/remoteData';

/**
 * Hook that returns functions for tracking impressions and conversions in our
 * multi-armed bandit A/B tests.
 *
 * Use the abTestStore under the hood to get the variant for the given A/B
 * test.
 *
 * @param abTestName - The name of the A/B test to track.
 */
export const useAbTestTracking = (
  abTestName: string,
): {
  variantNameRemoteData: RemoteData<string, string>;
  registerImpression: () => Promise<void>;
  registerConversion: () => Promise<void>;
} => {
  const abTests = useAbTestStore(state => state.abTests);

  const variantNameRemoteData = remoteDataSelect(
    abTests,
    data => data[abTestName]?.variant,
  );

  /**
   * Memoise the variant name to avoid redefining the registerImpression and
   * registerConversion functions. This is to avoid double-counting impressions
   * with unnecessary re-renders.
   */
  const variantName = useMemo(
    () =>
      variantNameRemoteData.type === 'Success'
        ? variantNameRemoteData.data
        : null,
    [variantNameRemoteData],
  );

  const registerImpression = useCallback(async () => {
    if (!variantName) {
      return;
    }

    try {
      await fetchRegisterImpression(abTestName, variantName);
    } catch (error) {
      ErrorTracking.track(error);
    }
  }, [abTestName, variantName]);

  const registerConversion = useCallback(async () => {
    if (!variantName) {
      return;
    }

    try {
      await fetchRegisterConversion(abTestName, variantName);
    } catch (error) {
      ErrorTracking.track(error);
    }
  }, [abTestName, variantName]);

  return {
    variantNameRemoteData,
    registerImpression,
    registerConversion,
  };
};
